import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { ErrorMessage, Field, Label } from '@/components/ui/catalyst/fieldset';
import { Input } from '@/components/ui/catalyst/input';
import { Select } from '@/components/ui/catalyst/select';
import {
  DestinationChannelType,
  ManualEvidenceType,
  SaveMode,
} from '@/types/provider-destinations';

import { SaveBehaviorToggle } from './SaveBehaviorToggle';
import { CHANNEL_LABELS } from './utils';

const phoneRegex = /^\+?1?\s*\(?[0-9]{3}\)?[-.\s]?[0-9]{3}[-.\s]?[0-9]{4}$/;
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  'DC', 'PR', 'VI', 'GU', 'AS', 'MP',
] as const;

const manualDestinationSchema = z
  .object({
    channel_type: z.nativeEnum(DestinationChannelType),
    value: z.string().optional().default(''),
    address_line1: z.string().optional().default(''),
    address_line2: z.string().optional().default(''),
    city: z.string().optional().default(''),
    state: z.string().optional().default(''),
    postal_code: z.string().optional().default(''),
    save_mode: z.nativeEnum(SaveMode),
    evidence_type: z.nativeEnum(ManualEvidenceType),
    evidence_detail: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.channel_type === DestinationChannelType.FAX) {
      if (!phoneRegex.test(data.value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Enter a valid fax/phone number',
          path: ['value'],
        });
      }
    }
    if (data.channel_type === DestinationChannelType.EMAIL) {
      if (!emailRegex.test(data.value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Enter a valid email address',
          path: ['value'],
        });
      }
    }
    if (data.channel_type === DestinationChannelType.MAIL) {
      if (!data.address_line1?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Address line 1 is required',
          path: ['address_line1'],
        });
      }
      if (!data.city?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'City is required',
          path: ['city'],
        });
      }
      if (!data.state?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'State is required',
          path: ['state'],
        });
      }
      if (!data.postal_code?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'ZIP code is required',
          path: ['postal_code'],
        });
      }
    }
    if (
      data.channel_type !== DestinationChannelType.MAIL &&
      (!data.value || !data.value.trim())
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Value is required',
        path: ['value'],
      });
    }
    if (
      data.evidence_type === ManualEvidenceType.OTHER &&
      (!data.evidence_detail || data.evidence_detail.trim().length === 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please describe the evidence source',
        path: ['evidence_detail'],
      });
    }
  });

export type ManualDestinationFormData = z.infer<typeof manualDestinationSchema>;

interface ManualDestinationFormProps {
  onSubmit: (data: ManualDestinationFormData) => void;
  onCancel: () => void;
  defaultChannelType?: DestinationChannelType;
}

const VALUE_PLACEHOLDERS: Record<DestinationChannelType, string> = {
  [DestinationChannelType.FAX]: '(555) 123-4567',
  [DestinationChannelType.EMAIL]: 'records@provider.com',
  [DestinationChannelType.MAIL]: '123 Main St, City, State ZIP',
  [DestinationChannelType.PORTAL]: 'https://portal.example.com',
};

const VALUE_INPUT_TYPES: Record<DestinationChannelType, string> = {
  [DestinationChannelType.FAX]: 'tel',
  [DestinationChannelType.EMAIL]: 'email',
  [DestinationChannelType.MAIL]: 'text',
  [DestinationChannelType.PORTAL]: 'url',
};

const EVIDENCE_LABELS: Record<ManualEvidenceType, string> = {
  [ManualEvidenceType.CALLED_PROVIDER]: 'Called provider',
  [ManualEvidenceType.PROVIDER_WEBSITE]: 'Provider website',
  [ManualEvidenceType.PREVIOUS_EXPERIENCE]: 'Previous experience',
  [ManualEvidenceType.OTHER]: 'Other',
};

export const ManualDestinationForm = ({
  onSubmit,
  onCancel,
  defaultChannelType = DestinationChannelType.FAX,
}: ManualDestinationFormProps) => {
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ManualDestinationFormData>({
    resolver: zodResolver(manualDestinationSchema),
    defaultValues: {
      channel_type: defaultChannelType,
      value: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      postal_code: '',
      save_mode: SaveMode.USE_ONCE,
      evidence_type: ManualEvidenceType.CALLED_PROVIDER,
      evidence_detail: '',
    },
  });

  const channelType = watch('channel_type');
  const evidenceType = watch('evidence_type');

  const renderMailFields = () => (
    <div className="space-y-3">
      <Field>
        <Label>Address line 1</Label>
        <Controller
          name="address_line1"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              value={field.value ?? ''}
              type="text"
              placeholder="123 Main St"
            />
          )}
        />
        {errors.address_line1 && (
          <ErrorMessage>{errors.address_line1.message}</ErrorMessage>
        )}
      </Field>

      <Field>
        <Label>Address line 2 (optional)</Label>
        <Controller
          name="address_line2"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              value={field.value ?? ''}
              type="text"
              placeholder="Suite 200"
            />
          )}
        />
      </Field>

      <div className="grid grid-cols-6 gap-3">
        <Field className="col-span-3">
          <Label>City</Label>
          <Controller
            name="city"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                value={field.value ?? ''}
                type="text"
                placeholder="City"
              />
            )}
          />
          {errors.city && (
            <ErrorMessage>{errors.city.message}</ErrorMessage>
          )}
        </Field>

        <Field className="col-span-1">
          <Label>State</Label>
          <Controller
            name="state"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value)}
              >
                <option value="">--</option>
                {US_STATES.map((st) => (
                  <option key={st} value={st}>
                    {st}
                  </option>
                ))}
              </Select>
            )}
          />
          {errors.state && (
            <ErrorMessage>{errors.state.message}</ErrorMessage>
          )}
        </Field>

        <Field className="col-span-2">
          <Label>ZIP code</Label>
          <Controller
            name="postal_code"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                value={field.value ?? ''}
                type="text"
                placeholder="12345"
              />
            )}
          />
          {errors.postal_code && (
            <ErrorMessage>{errors.postal_code.message}</ErrorMessage>
          )}
        </Field>
      </div>
    </div>
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      <Field>
        <Label>Channel type</Label>
        <Controller
          name="channel_type"
          control={control}
          render={({ field }) => (
            <Select
              value={field.value}
              onChange={(e) =>
                field.onChange(e.target.value as DestinationChannelType)
              }
            >
              {Object.values(DestinationChannelType).map((ct) => (
                <option key={ct} value={ct}>
                  {CHANNEL_LABELS[ct]}
                </option>
              ))}
            </Select>
          )}
        />
      </Field>

      {channelType === DestinationChannelType.MAIL ? (
        renderMailFields()
      ) : (
        <Field>
          <Label>{CHANNEL_LABELS[channelType]}</Label>
          <Controller
            name="value"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                type={
                  VALUE_INPUT_TYPES[channelType] as
                    | 'tel'
                    | 'email'
                    | 'url'
                    | 'text'
                }
                placeholder={VALUE_PLACEHOLDERS[channelType]}
              />
            )}
          />
          {errors.value && <ErrorMessage>{errors.value.message}</ErrorMessage>}
        </Field>
      )}

      <Field>
        <Label>Default preference</Label>
        <Controller
          name="save_mode"
          control={control}
          render={({ field }) => (
            <SaveBehaviorToggle value={field.value} onChange={field.onChange} />
          )}
        />
      </Field>

      <Field>
        <Label>How did you find this?</Label>
        <Controller
          name="evidence_type"
          control={control}
          render={({ field }) => (
            <Select
              value={field.value}
              onChange={(e) =>
                field.onChange(e.target.value as ManualEvidenceType)
              }
            >
              {Object.values(ManualEvidenceType).map((et) => (
                <option key={et} value={et}>
                  {EVIDENCE_LABELS[et]}
                </option>
              ))}
            </Select>
          )}
        />
      </Field>

      {evidenceType === ManualEvidenceType.OTHER && (
        <Field>
          <Label>Details</Label>
          <Controller
            name="evidence_detail"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                value={field.value ?? ''}
                type="text"
                placeholder="Describe how you found this destination"
              />
            )}
          />
          {errors.evidence_detail && (
            <ErrorMessage>{errors.evidence_detail.message}</ErrorMessage>
          )}
        </Field>
      )}

      <div className="flex items-center justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">Submit</Button>
      </div>
    </form>
  );
};
